import { useEffect, useRef, useState } from 'react';
import {
  CheckCircle2, ChevronLeft, ChevronRight, Edit2, Plus, Trash2,
} from 'lucide-react';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import { chargerConnectivityLabel, type ChargerConnectivity } from '@evi/shared';
import {
  submitSpInstall, getDisplayErrorMessage,
  type SpInstallBody, type SpInstallConnectorType, type SpInstallResult,
} from '@/lib/api';
import { buildSpInstallBody } from './spInstallBody';
import {
  CONNECTIVITY_CHOICE_ERROR, CONNECTIVITY_FIELD_LABEL, CONNECTIVITY_OPTIONS, connectivityTicked,
  connectivityToggled, type ConnectivityDraft,
} from '../network-submissions/connectivityChoice';
import { PlaceAutocomplete, type ResolvedAddress } from '@/components/ui/PlaceAutocomplete';
import { TurnstileGate, TURNSTILE_SITE_KEY } from '@/components/ui/TurnstileGate';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './SpRegisterInstall.module.css';
import { DeploymentPage } from '../network-submissions/DeploymentPage';
import { Switch } from '@/components/ui/Switch';

/**
 * Register a customer, location and physical units before programming.
 * Both registration forms use the commissioning lifecycle: qualified,
 * reviewed partners receive credentials immediately; others await an
 * operator. Connection is followed by per-plug checks and human launch.
 * Retries reuse the same request key and deployment.
 */

const HAS_GOOGLE = Boolean(import.meta.env['VITE_GOOGLE_MAPS_KEY']);
const GOOGLE_MAPS_KEY = import.meta.env['VITE_GOOGLE_MAPS_KEY'] as string | undefined;

// Mirrors the server's charger-item schema (installs.ts).
const SERIAL_RE = /^[A-Za-z0-9_-]{1,20}$/;
const CONNECTOR_TYPES: SpInstallConnectorType[] = ['J1772', 'NACS', 'CCS1'];
const MAX_CHARGERS = 50;

const STEPS = ['Customer', 'Location', 'Chargers', 'Review'] as const;

/**
 * Site type (Warren, 2026-09-30, SP origination rule): "Install wizard: add a
 * site-type choice (commercial, multifamily, residential). Residential cannot
 * be partner-attributed." A residential charger is registered by the
 * homeowner, so choosing it here hands off rather than submitting; the API
 * refuses it too (RESIDENTIAL_HOMEOWNER_REGISTERS).
 */
const SITE_TYPES = [
  { value: 'commercial', label: 'Commercial', hint: 'A business, workplace, hotel or public lot.' },
  { value: 'multifamily', label: 'Multifamily', hint: 'An apartment or condo building\u2019s shared chargers.' },
  { value: 'residential', label: 'Residential', hint: 'A single home. The homeowner registers it; it never counts or earns.' },
] as const;
type SiteType = (typeof SITE_TYPES)[number]['value'];
const RESIDENTIAL_HANDOFF = 'Residential chargers are registered by the homeowner in the EV Initiative app, and they never count toward your tier or earn commission. Ask the homeowner to add the charger under My Chargers.';

// Street View Static preview , same construction as the location
// hero preview on LocationDetailPage (size 640x320 @2x, fov 80,
// return_error_code so a no-coverage panorama 404s into onError
// instead of rendering Google's grey sadness tile).
function buildStreetViewUrl(
  coordinates: { lat: number; lng: number } | null,
  apiKey: string | undefined,
): string | null {
  if (!coordinates || !apiKey) return null;
  const params = new URLSearchParams({
    size: '640x320',
    scale: '2',
    fov: '80',
    pitch: '0',
    location: `${coordinates.lat},${coordinates.lng}`,
    return_error_code: 'true',
    key: apiKey,
  });
  return `https://maps.googleapis.com/maps/api/streetview?${params.toString()}`;
}

/** Pull the structured error code out of a ky HTTPError so the
 *  submit handler can branch on SERIAL_ALREADY_REGISTERED and the
 *  turnstile codes. Non-throwing; null on any parse edge. */
async function readErrorCode(err: unknown): Promise<string | null> {
  const response = (err as { response?: Response }).response;
  if (!response) return null;
  try {
    const body = await response.clone().json() as { error?: { code?: string } };
    return body?.error?.code ?? null;
  } catch {
    return null;
  }
}

interface ChargerRowDraft {
  serial: string;
  connector_types: SpInstallConnectorType[];
  max_power_kw: string;
  /** Drivers billed here. Defaults true , the common field case, and the
   *  only kind that advances the SP commission ordinal + earns a rate. */
  pay_to_use: boolean;
}

const emptyChargerRow = (): ChargerRowDraft => ({
  serial: '', connector_types: ['J1772'], max_power_kw: '', pay_to_use: true,
});

interface SpRegisterInstallPageProps {
  onNavigate: (to: string) => void;
}

export function SpRegisterInstallPage({ onNavigate }: SpRegisterInstallPageProps) {
  useRecentRoute('Register Install');

  // One idempotency key per wizard mount, reused across retries ,
  // see the module doc comment for why this must NOT be re-minted
  // on resubmit.
  const [idempotencyKey] = useState(
    () => crypto.randomUUID().replace(/-/g, '').slice(0, 32),
  );

  const [step, setStep] = useState(1);
  const [stepError, setStepError] = useState('');

  // ── Step 1 , customer ────────────────────────────────────────────
  // Sales Partners type the customer in; there is no list to pick from
  // (Warren, 2026-10-20). The "Existing customer" list was GET
  // /sp/customers: every tenant holding a charger attributed to the
  // partner, so it offered customers the partner never created (on
  // staging, EV Initiative's own tenant). The Api refuses an existing-
  // customer install into a tenant the partner did not create.
  const submitMode = 'new' as const;
  const tenantId = '';
  const [businessName, setBusinessName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  // ── Step 2 , location (AddLocationForm's HAS_GOOGLE + manualMode
  // fallback pattern) ──────────────────────────────────────────────
  const [locationName, setLocationName] = useState('');
  const [siteType, setSiteType] = useState<SiteType | ''>('');
  // Required per deployment (Warren, 2026-10-01); one choice for every unit.
  const [connectivity, setConnectivity] = useState<ConnectivityDraft>('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('US');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [manualMode, setManualMode] = useState(!HAS_GOOGLE);
  const [autocompleteResolved, setAutocompleteResolved] = useState(false);
  const [streetViewFailed, setStreetViewFailed] = useState(false);
  // Captured from Places so the Location stores google_place_id at
  // birth. editAddressField below clears it (with lat/lng) on any
  // manual edit: a hand-corrected address no longer matches the
  // picked place, and stale coordinates would pin the wrong building.
  const [placeId, setPlaceId] = useState('');

  const onPlaceSelected = (a: ResolvedAddress) => {
    setStreet(a.street);
    setCity(a.city);
    setState(a.state);
    setZip(a.zip);
    setCountry(a.country);
    setLat(String(a.lat));
    setLng(String(a.lng));
    setPlaceId(a.place_id ?? '');
    setAutocompleteResolved(true);
  };

  // Manual edits invalidate the picked place: drop the geodata so the
  // server geocodes the corrected address instead of keeping the old
  // building's pin and place id.
  const editAddressField = (set: (v: string) => void) => (v: string) => {
    set(v);
    if (placeId || lat || lng) {
      setPlaceId('');
      setLat('');
      setLng('');
      setAutocompleteResolved(false);
    }
  };

  const latN = parseFloat(lat);
  const lngN = parseFloat(lng);
  const coordinates = Number.isFinite(latN) && Number.isFinite(lngN)
    ? { lat: latN, lng: lngN }
    : null;
  const streetViewUrl = buildStreetViewUrl(coordinates, GOOGLE_MAPS_KEY);

  // A corrected address should re-try the preview after a miss.
  useEffect(() => {
    setStreetViewFailed(false);
  }, [lat, lng]);

  // ── Step 3 , chargers ────────────────────────────────────────────
  const [chargers, setChargers] = useState<ChargerRowDraft[]>([emptyChargerRow()]);

  const patchChargerRow = (index: number, patch: Partial<ChargerRowDraft>) => {
    setChargers((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  // ── Step 4 , review + submit ─────────────────────────────────────
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // 409 SERIAL_ALREADY_REGISTERED , rendered near the chargers
  // summary with a jump back to step 3, separate from generic errors.
  const [serialConflict, setSerialConflict] = useState('');
  const [result, setResult] = useState<SpInstallResult | null>(null);

  // ── Per-step validation, run before advancing ────────────────────
  const validateStep = (s: number): string => {
    if (s === 1) {
      if (!businessName.trim()) return 'Business name is required.';
      if (!contactName.trim()) return 'Contact name is required.';
      if (!/^\S+@\S+\.\S+$/.test(contactEmail.trim())) return 'Enter a valid contact email.';
      return '';
    }
    if (s === 2) {
      if (!siteType) return 'Choose the site type.';
      if (siteType === 'residential') return RESIDENTIAL_HANDOFF;
      if (!connectivity) return CONNECTIVITY_CHOICE_ERROR;
      if (!locationName.trim()) return 'Location name is required.';
      if (!street.trim()) return 'Street is required.';
      if (!city.trim()) return 'City is required.';
      const st = state.trim();
      if (st.length < 2 || st.length > 3) return 'State must be 2 or 3 characters.';
      if (!zip.trim()) return 'ZIP is required.';
      return '';
    }
    if (s === 3) {
      if (chargers.length === 0) return 'Add at least one charger.';
      const seen = new Set<string>();
      for (let i = 0; i < chargers.length; i += 1) {
        const row = chargers[i]!;
        const serial = row.serial.trim();
        if (!SERIAL_RE.test(serial)) {
          return `Charger ${i + 1}: serial must be 1 to 20 letters, numbers, dashes, or underscores.`;
        }
        if (seen.has(serial)) {
          return `Duplicate serial ${serial}. Each charger needs a unique serial.`;
        }
        seen.add(serial);
        const kw = Number(row.max_power_kw);
        if (!Number.isFinite(kw) || kw < 1 || kw > 350) {
          return `Charger ${i + 1}: max power must be between 1 and 350 kW.`;
        }
      }
      return '';
    }
    return '';
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) {
      setStepError(err);
      return;
    }
    setStepError('');
    setStep(step + 1);
  };

  const goBack = () => {
    setStepError('');
    setStep(step - 1);
  };

  const buildBody = (chosen: ChargerConnectivity): SpInstallBody => buildSpInstallBody({
    idempotencyKey,
    customerMode: submitMode,
    tenantId, businessName, contactName, contactEmail,
    locationName, siteType, street, city, state, zip, country, coordinates, placeId,
    connectivity: chosen,
    chargers,
    turnstileToken,
  });

  const handleSubmit = async () => {
    setSubmitError('');
    setSerialConflict('');
    // Step 2 refuses to advance without it; this only guards a stale state.
    if (!connectivity) { setSubmitError(CONNECTIVITY_CHOICE_ERROR); return; }
    setSubmitting(true);
    try {
      const data = await submitSpInstall(buildBody(connectivity));
      setResult(data);
    } catch (err) {
      const code = await readErrorCode(err);
      // The server verifies the Turnstile token BEFORE the serial
      // sweep, and Cloudflare tokens are single-use , so after ANY
      // failed submit the held token is spent. Reset unconditionally
      // or the designed fix-and-resubmit path always bounces off a
      // captcha failure first.
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      if (code === 'SERIAL_ALREADY_REGISTERED') {
        // Server message names the offending serials , show verbatim.
        setSerialConflict(await getDisplayErrorMessage(
          err, 'One or more serials are already registered to another account.',
        ));
      } else if (code === 'TURNSTILE_FAILED' || code === 'TURNSTILE_REQUIRED') {
        setSubmitError(await getDisplayErrorMessage(
          err, 'Captcha verification failed. Please try again.',
        ));
      } else {
        setSubmitError(await getDisplayErrorMessage(
          err, 'Failed to register the install. Please try again.',
        ));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Done screen ──────────────────────────────────────────────────
  if (result?.deployment_id) return <DeploymentPage deploymentId={result.deployment_id} />;

  if (result) {
    return <div className={styles.page}>
      <h1 className={styles.title}>Registration received</h1>
      <p>Open your deployments to confirm its status and access programming cards. Passwords are only revealed on the signed-in commissioning page; connecting does not approve launch.</p>
      <button type="button" className={styles.btnPrimary} onClick={() => onNavigate('/deployments')}>
        Open Deployment commissioning
      </button>
    </div>;
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Register Install</h1>
        <p className={styles.subtitle}>
          Register before programming or shipment. Get credentials, test each plug, then request launch approval.
        </p>
        <p className={styles.subtitle}>
          This wizard covers your customer, site and chargers. You do not also need to Submit a Location for the same install.
          {' '}<a href="/charger-install-guide.html" target="_blank" rel="noreferrer">Read the buyer and installer guide</a>.
        </p>
      </header>

      {/* Step indicator , simple numbered header, no stepper lib. */}
      <ol className={styles.stepper} aria-label="Wizard progress">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const stateClass = n < step ? styles.stepDone : n === step ? styles.stepCurrent : '';
          return (
            <li key={label} className={`${styles.step} ${stateClass}`}>
              <span className={styles.stepNum} aria-hidden>{n}</span>
              <span className={styles.stepLabel}>{label}</span>
            </li>
          );
        })}
      </ol>

      <div className={styles.card}>
        {stepError && <div className={styles.formError}>{stepError}</div>}

        {/* ── Step 1 , Customer ─────────────────────────────────── */}
        {step === 1 && (
          <>
            <p className={styles.hint}>
              Type in the customer for this install. We set up their account
              now. It activates, and their contact gets a sign-in link by
              email, when the first charger connects.
            </p>
              <>
                <div className={styles.field}>
                  <label className={`${styles.label} ${styles.required}`}>Business Name</label>
                  <input
                    className={styles.input}
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="e.g. Mission Hills Plaza LLC"
                    maxLength={200}
                  />
                </div>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label className={`${styles.label} ${styles.required}`}>Contact Name</label>
                    <input
                      className={styles.input}
                      type="text"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      placeholder="e.g. Dana Ortiz"
                      maxLength={120}
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={`${styles.label} ${styles.required}`}>Contact Email</label>
                    <input
                      className={styles.input}
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="dana@example.com"
                      maxLength={254}
                    />
                  </div>
                </div>
              </>
          </>
        )}

        {/* ── Step 2 , Location ─────────────────────────────────── */}
        {step === 2 && (
          <>
            <div className={styles.field}>
              <label className={`${styles.label} ${styles.required}`}>Site type</label>
              <div className={styles.radioGroup} role="radiogroup" aria-label="Site type">
                {SITE_TYPES.map((o) => (
                  <label key={o.value} className={styles.radioOption}>
                    <input
                      type="radio"
                      name="siteType"
                      checked={siteType === o.value}
                      onChange={() => setSiteType(o.value)}
                    />
                    <span>
                      <strong>{o.label}</strong>
                      <span className={styles.radioHint}>{o.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
              {siteType === 'residential' && (
                <p className={styles.radioHint} role="note">{RESIDENTIAL_HANDOFF}</p>
              )}
            </div>

            {siteType !== 'residential' && (
              <div className={styles.field}>
                <label className={`${styles.label} ${styles.required}`}>{CONNECTIVITY_FIELD_LABEL}</label>
                <div className={styles.radioGroup} role="group" aria-label={CONNECTIVITY_FIELD_LABEL}>
                  {CONNECTIVITY_OPTIONS.map((o) => (
                    <label key={o.value} className={styles.radioOption}>
                      <input
                        type="checkbox"
                        name={`connectivity-${o.value}`}
                        checked={connectivityTicked(connectivity, o.value)}
                        onChange={(e) => setConnectivity((d) => connectivityToggled(d, o.value, e.target.checked))}
                      />
                      <span>
                        <strong>{o.label}</strong>
                        <span className={styles.radioHint}>{o.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.field}>
              <label className={`${styles.label} ${styles.required}`}>Location Name</label>
              <input
                className={styles.input}
                type="text"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                placeholder="e.g. Mission Hills Plaza Garage"
                maxLength={200}
              />
            </div>

            {/* Address: Places autocomplete when the key is configured,
                manual fields otherwise (AddLocationForm pattern). */}
            {HAS_GOOGLE && !manualMode ? (
              <div className={styles.field}>
                <label className={`${styles.label} ${styles.required}`}>Address</label>
                <PlaceAutocomplete onSelect={onPlaceSelected} placeholder="Start typing an address..." />
                {autocompleteResolved && (
                  <div className={styles.resolvedCard}>
                    <div className={styles.resolvedText}>
                      <CheckCircle2 size={14} className={styles.resolvedIcon} aria-hidden />
                      <strong>{street}</strong>
                      <br />
                      <span className={styles.resolvedMuted}>{city}, {state} {zip} · {country}</span>
                      {coordinates && (
                        <>
                          <br />
                          <span className={styles.resolvedCoords}>
                            {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
                          </span>
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.editBtn}
                      onClick={() => setManualMode(true)}
                      title="Edit fields manually"
                    >
                      <Edit2 size={12} /> Edit
                    </button>
                  </div>
                )}
                <p className={styles.hint}>
                  Powered by Google Places.{' '}
                  <button type="button" className={styles.linkBtn} onClick={() => setManualMode(true)}>
                    Enter manually
                  </button>{' '}
                  if you prefer.
                </p>
              </div>
            ) : (
              <>
                {manualMode && HAS_GOOGLE && (
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={() => { setManualMode(false); setAutocompleteResolved(false); }}
                  >
                    ← Back to address search
                  </button>
                )}

                <div className={styles.field}>
                  <label className={`${styles.label} ${styles.required}`}>Street</label>
                  <input
                    className={styles.input}
                    type="text"
                    value={street}
                    onChange={(e) => editAddressField(setStreet)(e.target.value)}
                    placeholder="123 Main St"
                  />
                </div>
                <div className={styles.row3}>
                  <div className={styles.field}>
                    <label className={`${styles.label} ${styles.required}`}>City</label>
                    <input className={styles.input} type="text" value={city} onChange={(e) => editAddressField(setCity)(e.target.value)} />
                  </div>
                  <div className={styles.field}>
                    <label className={`${styles.label} ${styles.required}`}>State</label>
                    <input className={styles.input} type="text" value={state} onChange={(e) => editAddressField(setState)(e.target.value)} placeholder="CA" minLength={2} maxLength={3} />
                  </div>
                  <div className={styles.field}>
                    <label className={`${styles.label} ${styles.required}`}>ZIP</label>
                    <input className={styles.input} type="text" value={zip} onChange={(e) => editAddressField(setZip)(e.target.value)} placeholder="91345" />
                  </div>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Country</label>
                  <input className={styles.input} type="text" value={country} onChange={(e) => editAddressField(setCountry)(e.target.value)} maxLength={2} />
                </div>
              </>
            )}

            {/* Street View preview once coordinates exist , onError
                hides it (return_error_code turns no-coverage into a
                4xx instead of a grey placeholder tile). */}
            {streetViewUrl && !streetViewFailed && (
              <img
                src={streetViewUrl}
                alt="Street View preview of the selected address"
                className={styles.streetViewImg}
                onError={() => setStreetViewFailed(true)}
              />
            )}
          </>
        )}

        {/* ── Step 3 , Chargers ─────────────────────────────────── */}
        {step === 3 && (
          <>
            <p className={styles.hint}>
              Enter each charger's serial exactly as printed on the unit.
              The serial becomes its OCPP charger id.
            </p>
            <div className={styles.chargerList}>
              <div className={styles.chargerRowHeader}>
                <span>Serial</span>
                <span>Connector</span>
                <span>Max Power (kW)</span>
                <span />
              </div>
              {chargers.map((row, i) => (
                <div key={i} className={styles.chargerRowGroup}>
                  <div className={styles.chargerRow}>
                  <input
                    className={`${styles.input} ${styles.serialInput}`}
                    type="text"
                    value={row.serial}
                    onChange={(e) => patchChargerRow(i, { serial: e.target.value })}
                    placeholder="e.g. AUT-0491"
                    maxLength={20}
                    aria-label={`Charger ${i + 1} serial`}
                  />
                  <select
                    className={styles.select}
                    value={row.connector_types[0]}
                    onChange={(e) => patchChargerRow(i, { connector_types: [e.target.value as SpInstallConnectorType, ...row.connector_types.slice(1)] })}
                    aria-label={`Charger ${i + 1} plug 1 type`}
                  >
                    {CONNECTOR_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <input
                    className={styles.input}
                    type="number"
                    min={1}
                    max={350}
                    step="any"
                    value={row.max_power_kw}
                    onChange={(e) => patchChargerRow(i, { max_power_kw: e.target.value })}
                    placeholder="e.g. 19.2"
                    aria-label={`Charger ${i + 1} max power in kilowatts`}
                  />
                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() => setChargers((rows) => rows.filter((_, j) => j !== i))}
                    disabled={chargers.length <= 1}
                    title="Remove charger"
                    aria-label={`Remove charger ${i + 1}`}
                  >
                    <Trash2 size={14} />
                  </button>
                  </div>
                  {row.connector_types.slice(1).map((type, j) => <label key={j} className={styles.field}>
                    Plug {j + 2} type
                    <select className={styles.select} value={type} aria-label={`Charger ${i + 1} plug ${j + 2} type`}
                      onChange={e => patchChargerRow(i, { connector_types: row.connector_types.map((t, n) => n === j + 1 ? e.target.value as SpInstallConnectorType : t) })}>
                      {CONNECTOR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </label>)}
                  <p className={styles.hint}>List every physical plug in its OCPP connector order. Each plug needs its own test.</p>
                  <button type="button" className={styles.addBtn} disabled={row.connector_types.length >= 8}
                    onClick={() => patchChargerRow(i, { connector_types: [...row.connector_types, row.connector_types[0] ?? 'J1772'] })}>Add plug to charger {i + 1}</button>
                  {row.connector_types.length > 1 && <button type="button" className={styles.addBtn}
                    onClick={() => patchChargerRow(i, { connector_types: row.connector_types.slice(0, -1) })}>Remove last plug</button>}
                  <Switch
                    className={styles.payToUseToggle}
                    checked={row.pay_to_use}
                    onChange={(e) => patchChargerRow(i, { pay_to_use: e.target.checked })}
                    aria-label={`Charger ${i + 1} is pay-to-use`}
                    label="Pay-to-use: drivers are billed for charging here. Switch off for a free amenity charger (no commission)."
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              className={styles.addBtn}
              onClick={() => setChargers((rows) => [...rows, emptyChargerRow()])}
              disabled={chargers.length >= MAX_CHARGERS}
            >
              <Plus size={14} /> Add Charger
            </button>
          </>
        )}

        {/* ── Step 4 , Review + submit ──────────────────────────── */}
        {step === 4 && (
          <>
            {submitError && <div className={styles.formError}>{submitError}</div>}

            <div className={styles.reviewBlock}>
              <p className={styles.reviewLabel}>Customer</p>
              <p className={styles.reviewValue}>
                {businessName.trim()}
                <br />
                <span className={styles.reviewMuted}>
                  {contactName.trim()} · {contactEmail.trim()}
                </span>
              </p>
            </div>

            <div className={styles.reviewBlock}>
              <p className={styles.reviewLabel}>Location</p>
              <p className={styles.reviewValue}>
                {locationName.trim()}
                {siteType && <span className={styles.reviewMuted}> · {SITE_TYPES.find((o) => o.value === siteType)?.label}</span>}
                <br />
                <span className={styles.reviewMuted}>
                  {street.trim()}, {city.trim()}, {state.trim().toUpperCase()} {zip.trim()}
                  {country.trim() ? ` · ${country.trim().toUpperCase()}` : ''}
                </span>
                {coordinates && (
                  <>
                    <br />
                    <span className={styles.reviewMuted}>
                      {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
                    </span>
                  </>
                )}
              </p>
            </div>

            <div className={styles.reviewBlock}>
              <p className={styles.reviewLabel}>Connection</p>
              <p className={styles.reviewValue}>{chargerConnectivityLabel(connectivity)}</p>
            </div>

            <div className={styles.reviewBlock}>
              <p className={styles.reviewLabel}>
                Chargers ({chargers.length})
              </p>
              <ul className={styles.reviewList}>
                {chargers.map((c, i) => (
                  <li key={i} className={styles.reviewChargerRow}>
                    <code className={styles.serialPill}>{c.serial.trim()}</code>
                    <span className={styles.reviewMuted}>
                      {c.connector_types.map((t, n) => `Plug ${n + 1}: ${t}`).join(', ')} · {c.max_power_kw} kW · {c.pay_to_use ? 'Pay-to-use' : 'Free'}
                    </span>
                  </li>
                ))}
              </ul>
              {serialConflict && (
                <div className={styles.formError}>
                  {serialConflict}
                  <div>
                    <button
                      type="button"
                      className={styles.conflictBtn}
                      onClick={() => { setSerialConflict(''); setStepError(''); setStep(3); }}
                    >
                      Fix serials
                    </button>
                  </div>
                </div>
              )}
            </div>

            <TurnstileGate
              ref={turnstileRef}
              posture="open"
              onToken={setTurnstileToken}
              className={styles.turnstile}
            />
          </>
        )}

        {/* ── Back / Next navigation ────────────────────────────── */}
        <div className={styles.navRow}>
          {step > 1 ? (
            <button type="button" className={styles.btnSecondary} onClick={goBack} disabled={submitting}>
              <ChevronLeft size={14} /> Back
            </button>
          ) : (
            <span />
          )}
          {step < STEPS.length ? (
            <button type="button" className={styles.btnPrimary} onClick={goNext}>
              Next <ChevronRight size={14} />
            </button>
          ) : (
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handleSubmit}
              disabled={submitting || (Boolean(TURNSTILE_SITE_KEY) && !turnstileToken)}
            >
              {submitting ? 'Registering...' : 'Register Install'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
